// Frontend-only types - no server dependencies

export enum UserRole {
  COACH = 'COACH',
  CLUB_ADMIN = 'CLUB_ADMIN',
  PARENT = 'PARENT'
}

/**
 * Age group options for teams
 */
export const AGE_GROUPS = [
  'U7 and below',
  'U8',
  'U9',
  'U10',
  'U11',
  'U12',
  'U13',
  'U14',
  'U15',
  'U16',
  'U17',
  'U18',
  'U19',
  'U20',
  'U21',
  'Seniors'
] as const;

export type AgeGroup = typeof AGE_GROUPS[number];

/**
 * Generate season options
 * Starts from 2025/26 (current season) and adds future seasons
 */
export function generateSeasonOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const seasons: string[] = [];
  
  // Start from 2025/26 (current season for early adopters)
  seasons.push('2025/26');
  
  // Add future seasons starting from 2026/27
  const startYear = Math.max(2026, currentYear);
  for (let i = 0; i < 4; i++) {
    const year = startYear + i;
    const seasonStr = `${year}/${(year + 1).toString().slice(-2)}`;
    // Avoid duplicates if current year is 2025 or earlier
    if (!seasons.includes(seasonStr)) {
      seasons.push(seasonStr);
    }
  }
  
  return seasons;
}

/**
 * FA Future Fit guidelines for team formats based on age group
 * Applies to seasons 2026/27 and later
 */
export interface FAFutureFitFormat {
  ageGroup: string;
  format: string;
  playersPerSide: number;
}

export const FA_FUTURE_FIT_FORMATS: FAFutureFitFormat[] = [
  { ageGroup: 'U7 and below', format: '3v3', playersPerSide: 3 },  // New format
  { ageGroup: 'U8', format: '5v5', playersPerSide: 5 },            // Unchanged
  { ageGroup: 'U9', format: '5v5', playersPerSide: 5 },            // Retained longer (was 7v7)
  { ageGroup: 'U10', format: '7v7', playersPerSide: 7 },           // Starts later (was 7v7)
  { ageGroup: 'U11', format: '7v7', playersPerSide: 7 },           // Retained longer (was 9v9)
  { ageGroup: 'U12', format: '9v9', playersPerSide: 9 },           // Starts later (was 9v9)
  { ageGroup: 'U13', format: '9v9', playersPerSide: 9 },           // Retained longer (was 11v11)
  { ageGroup: 'U14', format: '11v11', playersPerSide: 11 },        // Starts later (was 11v11)
  { ageGroup: 'U15', format: '11v11', playersPerSide: 11 },
  { ageGroup: 'U16', format: '11v11', playersPerSide: 11 },
  { ageGroup: 'U17', format: '11v11', playersPerSide: 11 },
  { ageGroup: 'U18', format: '11v11', playersPerSide: 11 },
  { ageGroup: 'U19', format: '11v11', playersPerSide: 11 },
  { ageGroup: 'U20', format: '11v11', playersPerSide: 11 },
  { ageGroup: 'U21', format: '11v11', playersPerSide: 11 },
  { ageGroup: 'Seniors', format: '11v11', playersPerSide: 11 }
];

/**
 * Get FA Future Fit format for a given age group
 */
export function getFAFutureFitFormat(ageGroup: string): FAFutureFitFormat | undefined {
  return FA_FUTURE_FIT_FORMATS.find(format => format.ageGroup === ageGroup);
}

/**
 * Check if FA Future Fit guidelines apply to a given season
 * Guidelines apply from 2026/27 onwards
 */
export function isFAFutureFitSeason(season: string): boolean {
  const match = season.match(/^(\d{4})\/\d{2}$/);
  if (!match) return false;
  
  const startYear = parseInt(match[1], 10);
  return startYear >= 2026;
}

/**
 * Get team format information for display
 */
export function getTeamFormatInfo(ageGroup: string, season: string): string {
  if (!isFAFutureFitSeason(season)) {
    return ''; // No format info for seasons before 2026/27
  }
  
  const format = getFAFutureFitFormat(ageGroup);
  if (!format) {
    return '';
  }
  
  return `FA Future Fit: ${format.format}`;
}

export interface User {
  id: string;
  email: string;
  role: UserRole;
  profile: {
    firstName: string;
    lastName: string;
    clubId?: string;
  };
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface TeamFormation {
  formationId: string;
  notation: string;
  name: string;
  isCustom: boolean;
}

export type TeamGender = 'Male' | 'Female' | 'Mixed';

export type SurfaceType = 'Grass' | 'Astro Turf' | 'Hard Surface' | 'Indoor';

export interface TrainingSession {
  id: string;
  dayOfWeek: string;
  location: string;
  startTime: string;
  endTime: string;
  surfaceType: SurfaceType;
}

export interface Team {
  id: string;
  name: string;
  ageGroup: string;
  season: string;
  coachId: string;
  clubId?: string;
  gender?: TeamGender;
  assessmentInterval: number;
  trainingSessions?: TrainingSession[];
  logoUrl?: string;
  assistantCoachName?: string;
  assistantCoachRole?: string;
  
  // Formation configuration (optional but recommended)
  primaryInPossessionFormation?: TeamFormation;
  primaryOutOfPossessionFormation?: TeamFormation;
  secondaryInPossessionFormation?: TeamFormation;
  secondaryOutOfPossessionFormation?: TeamFormation;
  
  createdAt: Date;
  updatedAt: Date;
  // Coach collaboration fields
  mainCoach?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  collaboratingCoach?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  pendingInvitation?: {
    email: string;
    firstName: string;
    lastName: string;
    expiresAt: string;
  } | null;
  userRole?: 'MAIN_COACH' | 'COLLABORATING_COACH';
}

export interface CreateTeamRequest {
  name: string;
  ageGroup: string;
  season: string;
  gender?: TeamGender;
  assessmentInterval?: number;
  trainingSessions?: TrainingSession[];
  primaryInPossessionFormation?: TeamFormation;
  primaryOutOfPossessionFormation?: TeamFormation;
  secondaryInPossessionFormation?: TeamFormation;
  secondaryOutOfPossessionFormation?: TeamFormation;
}

export interface UpdateTeamRequest {
  name?: string;
  ageGroup?: string;
  season?: string;
  assessmentInterval?: number;
  logoUrl?: string;
  collaboratingCoachId?: string;
  lastInvitationSentAt?: Date;
  primaryInPossessionFormation?: TeamFormation;
  primaryOutOfPossessionFormation?: TeamFormation;
  secondaryInPossessionFormation?: TeamFormation;
  secondaryOutOfPossessionFormation?: TeamFormation;
}

export interface PlayerGuardian {
  firstName: string;
  lastName: string;
  email: string;
}

export interface Player {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: Date;
  position: string;
  fanNumber?: string;
  nationality?: string;
  photoUrl?: string;
  contactEmail?: string;
  guardians?: PlayerGuardian[];
  teamId: string;
  parentIds: string[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export enum AssessmentType {
  INITIAL = 'INITIAL',
  FOLLOW_UP = 'FOLLOW_UP',
  PERIODIC = 'PERIODIC',
  FINAL = 'FINAL'
}

export interface FAScores {
  technical: {
    // Outfield player attributes
    ball_control?: number;
    passing_accuracy?: number;
    positioning?: number;
    tactical_awareness?: number;
    shooting?: number;
    dribbling?: number;
    // Goalkeeper-specific attributes
    shot_stopping?: number;
    handling?: number;
    distribution?: number;
    aerial_ability?: number;
    one_on_one?: number;
    command_of_area?: number;
  };
  physical: {
    speed: number;
    endurance: number;
    strength: number;
    agility: number;
    coordination: number;
    balance: number;
  };
  psychological: {
    confidence: number;
    resilience: number;
    decision_making: number;
    concentration: number;
    motivation: number;
    composure: number;
  };
  social: {
    teamwork: number;
    communication: number;
    respect: number;
    leadership: number;
    coachability: number;
    sportsmanship: number;
  };
}

export interface Assessment {
  id: string;
  playerId: string;
  assessorId: string;
  type: AssessmentType;
  scores: FAScores;
  notes?: string;
  isComplete: boolean;
  emailSentAt?: string;
  assessmentDate: Date;
  createdAt: Date;
  updatedAt: Date;
}

export enum ReportType {
  PLAYER = 'PLAYER',
  TEAM = 'TEAM',
  CLUB = 'CLUB'
}

export interface Report {
  id: string;
  type: ReportType;
  title: string;
  generatedBy: string;
  generatedFor?: string;
  data: any;
  fileUrl?: string;
  expiresAt: Date;
  createdAt: Date;
}


// ─── Club Administration Types ────────────────────────────────────────────────

export enum ClubRole {
  CLUB_ADMIN = 'CLUB_ADMIN',
  COACH = 'COACH',
  PARENT = 'PARENT',
  DIRECTOR_OF_FOOTBALL = 'DIRECTOR_OF_FOOTBALL',
}

export interface Club {
  id: string;
  name: string;
  affiliatedFA: string;
  address: string;
  createdByUserId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export enum SubscriptionTier {
  STARTER = 'STARTER',
  STANDARD = 'STANDARD',
  PREMIUM = 'PREMIUM',
}

export enum SubscriptionStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  CANCELLED = 'CANCELLED',
  PAST_DUE = 'PAST_DUE',
}

export interface ClubSubscription {
  clubId: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  teamLimit: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  discountCodeApplied?: string;
  createdAt: string;
  updatedAt: string;
}

export enum ClubInvitationStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  EXPIRED = 'EXPIRED',
  REVOKED = 'REVOKED',
}

export interface ClubInvitation {
  id: string;
  clubId: string;
  invitedEmail: string;
  invitedFirstName: string;
  invitedLastName: string;
  invitedRole: ClubRole;
  linkedPlayerId?: string;
  tokenHash: string;
  expiresAt: string;
  status: ClubInvitationStatus;
  invitedByUserId: string;
  acceptedByUserId?: string;
  acceptedAt?: string;
  createdAt: string;
}

export interface ClubContact {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: ClubRole;
  associatedTeamId?: string;
  associatedTeamName?: string;
  associatedPlayerId?: string;
  associatedPlayerName?: string;
  joinedAt: string;
}

export interface ClubDashboardStats {
  totalTeams: number;
  totalCoaches: number;
  totalPlayers: number;
  subscriptionStatus: SubscriptionStatus | null;
  teamLimit: number;
}

// ──────────────────────────────────────────────
// Player Development types
// ──────────────────────────────────────────────

export interface VideoRecommendation {
  videoId: string;
  title: string;
  thumbnailUrl: string;
  channelName: string;
  mappedAttribute: string;
}

export interface TrainingTipResponse {
  tipId: string;
  title: string;
  description: string;
  exampleDrill: string;
}

export interface Recommendation {
  corner: 'technical' | 'physical' | 'psychological' | 'social';
  attribute: string;
  currentScore: number;
  targetDescription: string;
  videos: VideoRecommendation[];
  trainingTips: TrainingTipResponse[];
}
